import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useTheme } from '../context/ThemeContext';

const testimonialsData = [
  {
    id: 1,
    author: "Marco Rodrigues",
    role: "Data Scientist",
    testimonial: "As a former microelectronics engineer, transitioning to IT presented a big challenge: proving my data skills without prior professional experience. Desights' challenges became the game-changer. They allowed me to showcase my data science knowledge. Every challenge I conquer is an opportunity to demonstrate my capabilities and fuel my career growth.",
    // image: "https://desights.ai/wp-content/uploads/2024/05/marco.webp",
    image: "/assets/marco.webp"
  },
  {
    id: 2,
    author: "Ahan M R",
    role: "Applied Scientist @Amazon Alexa",
    testimonial: "Desights has been a game-changer for me, ensuring a smooth user experience throughout the competition with easy onboarding and seamless wallet integration. The non-custodial publishing of my assets to Arweave permaweb, along with a secure smart contract account, truly enhances the user experience.",
    // image: "https://desights.ai/wp-content/uploads/2024/05/ahan.jpeg",
    image: "/assets/ahan.jpeg"
  },
  {
    id: 3,
    author: "Dominikus Brian",
    role: "Founder & CTO of DreamBrook Labs",
    testimonial: "Desights is where AI challenges and talent meet. Unlike many other Web3 platforms that are often plagued by UI and UX issues, Desights AI offers smooth experiences and workflows for both users and organizations. We are eager to continue participating in the platform's rapid growth and ready to launch our very first AI crowdsourcing campaign on Desights!",
    // image: "https://desights.ai/wp-content/uploads/2024/05/Domi_PP_v1_logo_crop.webp",
    image: "/assets/Domi.webp"
  },
];

const Card = ({ author, role, testimonial, image, isDarkMode }) => {
  const firstLetter = author ? author.charAt(0).toUpperCase() : "";

  return (
    <div className="gradient-border-box h-full">
      <div className={`relative bg-transparent backdrop-blur-[20px] rounded-[20px] sm:rounded-[30px] p-2.5 xs:p-3 sm:p-6 h-full w-full transition-all duration-300 flex flex-col`}>
        {/* Author Info Section - Fixed Height */}
        <div className="flex items-start xs:items-center gap-2 xs:gap-3 mb-2 xs:mb-3 sm:mb-5 pb-2 xs:pb-3 sm:pb-4 border-b border-gray-200/10">
          {image ? (
            <img 
              src={image} 
              alt={author} 
              className={`w-7 h-7 xs:w-8 xs:h-8 sm:w-12 sm:h-12 rounded-full object-cover border-2 flex-shrink-0 ${
                isDarkMode ? 'border-white/20' : 'border-black/20'
              }`}
            />
          ) : (
            <div className={`w-7 h-7 xs:w-8 xs:h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm xs:text-base sm:text-xl font-semibold bg-gradient-to-r from-[#F49294] to-[#FBD13A] text-white border-2 flex-shrink-0 ${
              isDarkMode ? 'border-white/20' : 'border-black/20'
            }`}>
              {firstLetter}
            </div>
          )}
          <div className="flex flex-col justify-center min-h-[40px] sm:min-h-[48px]">
            <h3 className={`text-[11px] xs:text-xs sm:text-base font-semibold ${isDarkMode ? 'text-white/95' : 'text-black/95'}`}>
              {author}
            </h3>
            <p className={`text-[9px] xs:text-[10px] sm:text-sm ${isDarkMode ? 'text-white/70' : 'text-black/70'}`}>
              {role}
            </p>
          </div>
        </div>

        {/* Testimonial Text - Flex Grow */}
        <div className="flex-grow overflow-hidden">
          <p className={`text-[11px] xs:text-xs sm:text-base leading-[1.4] xs:leading-relaxed ${
            isDarkMode ? 'text-white/90' : 'text-black/90'
          } line-clamp-6 xs:line-clamp-5 sm:line-clamp-[8]`}>
            {testimonial}
          </p>
        </div>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const { isDarkMode } = useTheme();
  const [width, setWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const controls = useAnimation();
  const tickerRef = useRef(null);

  // Move startScrollAnimation into useCallback to memoize it
  const startScrollAnimation = useCallback(() => {
    if (!tickerRef.current) return;
    
    controls.start({
      x: -width,
      transition: {
        duration: 30,
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop",
        repeatDelay: 0,
      },
    });
  }, [controls, width]);

  // Set width when component mounts and when window resizes
  useEffect(() => {
    const updateWidth = () => {
      if (tickerRef.current) {
        setWidth(tickerRef.current.offsetWidth / 2);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Update the useEffect to include startScrollAnimation in dependencies
  useEffect(() => {
    if (width > 0) {
      startScrollAnimation();
    }
  }, [width, startScrollAnimation]);

  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      controls.stop();
    };
  }, [controls]);

  useEffect(() => {
    const cssStyles = `
      .gradient-border-box {
        position: relative;
        padding: 0.1rem;
        width: 100%;
        height: 100%;
      }

      .gradient-border-box::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 30px;
        border: 2px solid transparent;
        background: linear-gradient(180deg, #F49294 0%, #FBD13A 50%, ${isDarkMode ? "#000000" : "#FFFFFF"} 100%) border-box;
        -webkit-mask: 
            linear-gradient(#fff 0 0) padding-box, 
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out;
        mask-composite: exclude;
        pointer-events: none;
      }
    `;

    const styleSheet = document.createElement("style");
    styleSheet.innerText = cssStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, [isDarkMode]);

  return (
    <section className={`relative py-12 sm:py-16 md:py-24 px-0 sm:px-4 ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`}>
      {/* Heart Shape Background */}
      <div className="absolute inset-x-0 -top-24 -bottom-24 max-w-[1300px] mx-auto">
        <div className="relative w-full h-full overflow-hidden">
          <svg 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[800px] w-[120%] sm:w-[90%] max-w-[1000px]"
            viewBox="0 0 1000 1000"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Light mode gradient */}
              <linearGradient id="heartBorderLight" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#FCD535' }} />
                <stop offset="50%" style={{ stopColor: '#F59198' }} />
                <stop offset="100%" style={{ stopColor: '#FFFFFF' }} />
              </linearGradient>
              
              {/* Dark mode gradient */}
              <linearGradient id="heartBorderDark" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#FCD535' }} />
                <stop offset="50%" style={{ stopColor: '#F59198' }} />
                <stop offset="100%" style={{ stopColor: '#000000' }} />
              </linearGradient>

              {/* Fill gradient with opacity - Light mode */}
              <linearGradient id="heartFillLight" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#FCD535', stopOpacity: '0.05' }} />
                <stop offset="50%" style={{ stopColor: '#F59198', stopOpacity: '0.05' }} />
                <stop offset="100%" style={{ stopColor: '#000000', stopOpacity: '0.05' }} />
              </linearGradient>

              {/* Fill gradient with opacity - Dark mode */}
              <linearGradient id="heartFillDark" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#FCD535', stopOpacity: '0.15' }} />
                <stop offset="50%" style={{ stopColor: '#F59198', stopOpacity: '0.15' }} />
                <stop offset="100%" style={{ stopColor: '#000000', stopOpacity: '0.15' }} />
              </linearGradient>
            </defs>

            {/* Heart path with gradient stroke and fill */}
            <path
              d="M500 888.72c-47.88-23.94-441.75-227.37-441.75-510.3C58.25 213.95 175.95 116 297.62 116c82.14 0 157.41 49.25 202.38 126.47C544.97 165.25 620.24 116 702.38 116c121.67 0 239.37 97.95 239.37 262.42 0 282.93-393.87 486.36-441.75 510.3z"
              stroke={`url(#heartBorder${isDarkMode ? 'Dark' : 'Light'})`}
              strokeWidth="2"
              fill={`url(#heartFill${isDarkMode ? 'Dark' : 'Light'})`}
            />
          </svg>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-8 sm:mb-12 md:mb-16 px-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className={`text-[20px] sm:text-[24px] md:text-[32px] lg:text-[36px] font-bold ${
            isDarkMode ? 'text-white' : 'text-black'
          } mb-4`}>
            What Our Community Is
            <br className="sm:hidden" />
            <span className="hidden sm:inline"> </span>
            Saying About Us
          </h2>
        </motion.div>

        {/* Slider Container */}
        <div className="w-full overflow-hidden">
          <div 
            className="w-full overflow-hidden px-4"
            onMouseEnter={() => {
              if (!isDragging) {
                controls.stop();
              }
            }}
            onMouseLeave={() => {
              if (!isDragging) {
                startScrollAnimation();
              }
            }}
          >
            <motion.div
              className="flex relative w-max bg-transparent will-change-transform"
              animate={controls}
              initial={{ x: 0 }}
              drag="x"
              dragConstraints={{
                left: -width,
                right: 0,
              }}
              dragElastic={0.2}
              onDragStart={() => {
                setIsDragging(true);
                controls.stop();
              }}
              onDragEnd={(event, info) => {
                setIsDragging(false);
                if (Math.abs(info.velocity.x) < 100) {
                  startScrollAnimation();
                }
              }}
            >
              <motion.div
                ref={tickerRef}
                className="flex gap-3 sm:gap-5 px-2"
              >
                {[...testimonialsData, ...testimonialsData].map((testimonial, index) => (
                  <motion.div
                    key={`${testimonial.id}-${index}`}
                    className="flex-none w-[220px] xs:w-[280px] sm:w-[340px] md:w-[400px] max-w-[85vw] p-0.5 xs:p-1 h-[200px] xs:h-[260px] sm:h-[320px] md:h-[360px]"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card {...testimonial} isDarkMode={isDarkMode} />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection; 
